interface ScheduledEvent {
  readonly id: number;
  time: number;
  readonly delay: number;
  triggerAt: number;
  readonly repeat: boolean;
  readonly pausable: boolean;
  readonly callback?: () => void;
}

export class EventScheduler {
  private readonly interval: number;

  private nextEventId = 0;

  private readonly events: ScheduledEvent[] = [];

  public paused = false;

  constructor(targetInterval: number = 1000 / 60) {
    this.interval = targetInterval;
  }

  public update(deltaTime: number): void {
    for (let i = this.events.length - 1; i >= 0; i -= 1) {
      const event = this.events[i];
      if (this.paused && event.pausable) continue;

      event.time += deltaTime * this.interval;
      if (event.triggerAt <= event.time) {
        this.events.splice(i, 1);
        if (event.callback) event.callback();
        if (event.repeat) {
          event.triggerAt += event.delay;
          this.events.push(event);
        }
      }
    }
  }

  public addEvent(callback: () => void, delay: number, repeat = false, pausable = true): number {
    const event: ScheduledEvent = {
      id: this.nextEventId,
      time: 0,
      delay,
      triggerAt: delay,
      repeat,
      pausable,
      callback,
    };
    this.nextEventId += 1;
    this.events.push(event);
    return event.id;
  }

  public removeEvent(eventId: number, executeCallback = false): void {
    const index = this.events.findIndex(({ id }) => id === eventId);
    if (index === -1) return;
    const event = this.events[index];
    if (executeCallback && event.callback) event.callback();
    this.events.splice(index, 1);
  }

  public wait(delay: number, pausable = true): Promise<void> {
    return new Promise((resolve) => {
      this.addEvent(resolve, delay, false, pausable);
    });
  }

  public waitUntil(condition: () => boolean, interval: number): Promise<void> {
    return new Promise((resolve) => {
      const eventId = this.addEvent(
        () => {
          if (!condition()) return;
          this.removeEvent(eventId);
          resolve();
        },
        interval,
        true,
      );
    });
  }

  public reset(): void {
    this.events.length = 0;
  }
}
